import type { Freshness } from '../../types/explore';

/**
 * How old a pooled reading is.
 *
 * This exists because stale pooled data presented without a caveat is worse than no data at all:
 * an agency pitches a creator on numbers from six months ago, the brand checks, and the number is
 * wrong. They will remember that it came from us.
 *
 * The four states are deliberately distinguished by **words**, not only colour. Someone who cannot
 * tell amber from green still reads "3 months old", and the label is what makes the caveat
 * actionable rather than decorative.
 */

const LABELS: Record<Freshness, { text: string; className: string; title: string }> = {
  fresh: {
    text: 'Fresh',
    className: 'bg-[rgb(var(--pos))]/15 text-[rgb(var(--pos))]',
    title: 'Captured within the last 7 days.',
  },
  aging: {
    text: 'Aging',
    className: 'bg-[rgb(var(--warn))]/15 text-[rgb(var(--warn))]',
    title: 'Captured 7 to 30 days ago. Worth re-checking before you pitch.',
  },
  stale: {
    text: 'Stale',
    className: 'bg-[rgb(var(--danger))]/15 text-[rgb(var(--danger))]',
    title: 'Captured more than 30 days ago. Treat these numbers as indicative only.',
  },
  unverified: {
    text: 'Unverified',
    className: 'bg-glass-3 text-glass-text-3',
    title: 'We have never recorded metrics for this creator — not the same as having recorded zero.',
  },
};

export function relativeAge(capturedAt: string | null, now: Date = new Date()): string | null {
  if (!capturedAt) return null;
  const days = Math.floor((now.getTime() - new Date(capturedAt).getTime()) / 86_400_000);
  if (days < 0) return 'just now';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
}

export default function FreshnessBadge({
  freshness,
  capturedAt,
}: {
  freshness: Freshness;
  capturedAt: string | null;
}) {
  const meta = LABELS[freshness];
  const age = relativeAge(capturedAt);

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-medium ${meta.className}`}
      title={meta.title}
    >
      {meta.text}
      {age && <span className="font-normal opacity-80">· {age}</span>}
    </span>
  );
}
